import type { PerimeterSearchApi } from "@App/apis/graphql/perimeterSearch/interfaces";
import type { GaragePropertiesRequest } from "@App/apis/graphql/perimeterSearch/requests/garagePropertiesRequest";
import type { GaragePaginator } from "@App/apis/graphql/perimeterSearch/responses/garagePaginator";
import type { GaragePropertyPaginator } from "@App/apis/graphql/perimeterSearch/responses/garagePropertyPaginator";
import {
  GET_GARAGE_PROPERTIES,
  type GetGaragePropertiesResponse,
} from "@App/apis/graphql/perimeterSearch/v1/queries/getGarageProperties";
import { apolloClient } from "@App/graphql/client";
import type { Garage } from "@App/types";
import { SortOrderType } from "@App/types/enums";
import { normalizeApolloError } from "@App/utils/api/graphql/normalizeApolloError";
import { formatDate, toIsoString } from "@App/utils/dates";
import { removeNullFromObject } from "@App/utils/objectHelper";
import type { GaragesRequest } from "../requests/garagesRequest";
import { GET_GARAGES, type GetGaragesResponse } from "./queries/getGarageList";

export class PerimeterSearchService implements PerimeterSearchApi {
  public async getGarageProperties({
    id = null,
    garages = null,
    networks,
    services,
    showInFilter = null,
    showInList = null,
    showInDetailView = null,
    internalIdentifier = null,
  }: GaragePropertiesRequest): Promise<GaragePropertyPaginator> {
    const result = await apolloClient.query<GetGaragePropertiesResponse>({
      query: GET_GARAGE_PROPERTIES,
      variables: removeNullFromObject({
        id: id,
        garages: garages,
        networks: networks,
        services: services,
        showInFilter: showInFilter,
        showInList: showInList,
        showInDetailView: showInDetailView,
        internalIdentifier: internalIdentifier,
      }),
    });

    const err = normalizeApolloError(result);
    if (err) throw err;

    return result.data.garageProperties;
  }

  /**
   * Fetches a single garage based on the provided request parameters.
   * For now, this method just fetches the first garage from the list of garages matching the request.
   *
   * @param request GaragesRequest object containing parameters for the garage search.
   */
  public async getGarage(request: GaragesRequest): Promise<Garage> {
    const garagesPaginator = await this.getGarages({
      ...request,
      limit: 1,
    });
    if (garagesPaginator.data.length === 0) {
      throw new Error("Garage not found");
    }
    return garagesPaginator.data[0];
  }

  public async getGarages({
    id = null,
    ids = null,
    organizationId,
    lat,
    lon,
    appointmentsFrom,
    appointmentsTo,
    distance,
    search = null,
    brandId = null,
    generationId = null,
    motorizationId = null,
    gearboxId = null,
    bodyId = null,
    mileage = null,
    registrationDate = null,
    services,
    sortBy = SortOrderType.SMALLEST_DISTANCE,
    limit = 100,
    offset = 0,
    premiumServices = null,
    equipments = null,
    subRepairs = null,
    supportsBev = null,
    garageTypes = null,
    onlyBookable = false,
    driverId = null,
    supportsCommercialVehicles = null,
    garageChains = null,
    presetFilters = null,
    garageProperties = null,
    onlyPreferredPartners = null,
    networkId = null,
  }: GaragesRequest): Promise<GaragePaginator> {
    const result = await apolloClient.query<GetGaragesResponse>({
      query: GET_GARAGES,
      variables: {
        id: id ?? null,
        ids: ids ?? null,
        organization: organizationId,
        lat: lat,
        lon: lon,
        appointmentsFrom: appointmentsFrom ? toIsoString(appointmentsFrom) : null,
        appointmentsTo: appointmentsTo ? toIsoString(appointmentsTo) : null,
        distance: distance,
        search: search,
        brand: brandId,
        generation: generationId,
        motorization: motorizationId,
        body: bodyId,
        gearbox: gearboxId,
        mileage: mileage,
        registrationDate: registrationDate ? formatDate(registrationDate, "yyyy-MM-dd") : null,
        services: services,
        sortBy: sortBy,
        limit: limit,
        offset: offset,
        premiumServices: premiumServices,
        equipments: equipments,
        subRepairs: subRepairs,
        supportsBev: supportsBev,
        onlyBookable: onlyBookable,
        driverId: driverId ? driverId : null,
        garageTypes: garageTypes,
        supportsCommercialVehicles: supportsCommercialVehicles,
        garageChains: garageChains,
        presetFilters: presetFilters,
        garageProperties: garageProperties,
        onlyPreferredPartners: onlyPreferredPartners,
        networkId: networkId,
      },
    });

    const err = normalizeApolloError(result);
    if (err) throw err;

    if (!result.data.organization) throw new Error("Organization not found");
    if (!result.data.organization.garages) throw new Error("Garages not found");

    return result.data.organization.garages;
  }
}
